import { useEffect, useMemo, useSyncExternalStore } from 'react';

import { useSignalingStore } from '../../store/useSignalingStore';
import { getSignalingClient, type SignalingClient } from './SignalingClient';

const SIGNALING_URL: string =
  (import.meta.env.VITE_SIGNALING_URL as string | undefined) ?? 'ws://localhost:8443/ws/signal';

/**
 * Returns the singleton SignalingClient and keeps Zustand state in sync with
 * the underlying socket state. Auto-connects on first mount; the singleton
 * survives route changes.
 */
export function useSignaling(): SignalingClient {
  const client = useMemo(() => getSignalingClient(SIGNALING_URL), []);
  const setConnectionState = useSignalingStore((s) => s.setConnectionState);

  useEffect(() => {
    const off = client.onState((state) => setConnectionState(state));
    client.connect();
    return () => off();
  }, [client, setConnectionState]);

  return client;
}

/**
 * Read-only subscription to the client's state without the side effect of
 * (re-)connecting. Useful in components that only care about display.
 */
export function useSignalingState(): SignalingClient['getState'] extends () => infer R ? R : never {
  const client = useMemo(() => getSignalingClient(SIGNALING_URL), []);
  return useSyncExternalStore(
    (cb) => client.onState(cb),
    () => client.getState(),
    () => client.getState(),
  );
}
